import { Select, Table as BaseTable } from '@octano/global-ui';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import { Table as TableCollapsable } from '../../../../../../components/table-collapsable';
import SectionTitle from '../../../../../../components/text/SectionTitle';
import { SubsidyPeriod } from '../../../api/subsidy';
import { YEAR_OPTIONS } from '../../../constants';
import { useSubsidyColumns, YearRow } from '../../hooks/useSubsidyColumns';
import { useSubsidyPeriodColumns } from '../../hooks/useSubsidyPeriodColumns';
import type { PeriodRow } from '../../hooks/useSubsidyPeriodColumns';

const prefix = 'tariffAndTuition.subsidies';

interface FormValues {
  name: string;
  applyAutomatically: boolean;
  periodAmounts: { [key: number]: string };
}

interface SubsidyPeriodsSectionProps {
  periods: SubsidyPeriod[];
  loadedAmounts: { [key: number]: string };
  yearFilter: string;
  onYearFilterChange: (value: string) => void;
}

const SubsidyPeriodsSection = ({
  periods,
  loadedAmounts,
  yearFilter,
  onYearFilterChange,
}: SubsidyPeriodsSectionProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext<FormValues>();

  const columns = useSubsidyColumns();
  const periodColumns = useSubsidyPeriodColumns({ control });

  const yearRows: YearRow[] = useMemo(() => {
    const byYear = periods.reduce((acc, period) => {
      const year =
        period.academicYear ?? new Date(period.startDate).getFullYear();
      if (!acc[year]) {
        acc[year] = [];
      }
      acc[year].push(period);
      return acc;
    }, {} as Record<number, SubsidyPeriod[]>);

    return Object.entries(byYear)
      .sort(([a], [b]) => Number(b) - Number(a))
      .map(([year, yearPeriods]) => ({
        year: Number(year),
        periods: yearPeriods,
      }));
  }, [periods]);

  const getPeriodRowsForYear = useCallback(
    (yearRow: YearRow): PeriodRow[] => {
      return yearRow.periods.map((period) => ({
        ...period,
        defaultValue: loadedAmounts[period.id] || '',
      }));
    },
    [loadedAmounts],
  );

  const secondColumn = useMemo(
    () => ({
      tdClassName: 'px-4',
      columnName: 'periods',
      headerText: '',
      cellFormat: ({ row }: { row: YearRow }) => (
        <BaseTable<PeriodRow>
          borderless={false}
          color="transparent"
          striped
          columns={periodColumns}
          data={getPeriodRowsForYear(row)}
          noResultsText={t(`${prefix}.noPeriods`)}
        />
      ),
    }),
    [periodColumns, getPeriodRowsForYear, t],
  );

  return (
    <>
      <Row className="mb-3">
        <Col xs="12">
          <SectionTitle text={t(`${prefix}.periodsAndAmounts`)} />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs="12" md="6">
          <Select
            name="yearFilter"
            label={t(`${prefix}.year`)}
            options={YEAR_OPTIONS}
            value={YEAR_OPTIONS.find((o) => o.value === yearFilter)}
            onChange={(option: any) =>
              onYearFilterChange(option?.value || 'last5')
            }
          />
        </Col>
      </Row>

      <Row>
        <Col xs="12">
          <TableCollapsable<YearRow>
            columns={columns}
            data={yearRows}
            secondColumn={secondColumn}
            noResultsText={t(`${prefix}.noYears`)}
          />
        </Col>
      </Row>
    </>
  );
};

export default SubsidyPeriodsSection;
